import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, X, Instagram, ShoppingBag, ChevronDown } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import { SHOP } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

const LINKS = [
    { to: "/", label: "Beranda", id: "beranda" },
    { to: "/menu", label: "Menu", id: "menu" },
    { to: "/galeri", label: "Galeri", id: "galeri" },
    { to: "/kontak", label: "Kontak", id: "kontak" },
];

const MORE_LINKS = [
    { to: "/promo", label: "Promo", id: "promo" },
    { to: "/testimoni", label: "Testimoni", id: "testimoni" },
    { to: "/artikel", label: "Artikel", id: "artikel" },
    { to: "/faq", label: "FAQ", id: "faq" },
    { to: "/tentang", label: "Tentang Kami", id: "tentang" },
];

const ALL_LINKS = [...LINKS, ...MORE_LINKS];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="navbar"
                className="fixed inset-x-0 top-0 z-50 border-b border-ink/5 bg-white/80 backdrop-blur-xl"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        onClick={() => setOpen(false)}
                    >
                        <Logo />
                    </Link>

                    <nav className="hidden items-center gap-8 lg:flex">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                className={({ isActive }) =>
                                    `text-sm font-semibold transition-colors hover:text-accent ${
                                        isActive ? "text-accent" : "text-ink"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="nav-more"
                                    className="flex items-center gap-1 text-sm font-semibold text-ink transition-colors hover:text-accent"
                                >
                                    Lainnya <ChevronDown className="h-4 w-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                sideOffset={12}
                                className="w-52 rounded-2xl border border-ink/5 bg-white p-2 shadow-lift"
                            >
                                {MORE_LINKS.map((l) => (
                                    <DropdownMenuItem key={l.id} asChild>
                                        <Link
                                            to={l.to}
                                            data-testid={`nav-more-${l.id}`}
                                            className="cursor-pointer rounded-xl px-3 py-2.5 text-sm font-semibold text-ink"
                                        >
                                            {l.label}
                                        </Link>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <a
                            href={SHOP.gofood}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-cta-gofood"
                            onClick={() => trackEvent("cta_click", { cta: "gofood_nav" })}
                            className="rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-accent-deep hover:shadow-lg"
                        >
                            Pesan di GoFood
                        </a>
                    </nav>

                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label={open ? "Tutup menu" : "Buka menu"}
                        onClick={() => setOpen(!open)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 bg-white text-ink transition-colors hover:border-ink/25 lg:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
                    </button>
                </div>
            </header>

            {open && (
                <div
                    data-testid="nav-mobile-overlay"
                    className="fixed inset-0 z-40 flex flex-col justify-between bg-bg px-6 pb-10 pt-24 lg:hidden"
                >
                    <nav className="flex max-h-[62vh] flex-col gap-1 overflow-y-auto pr-2">
                        {ALL_LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                data-testid={`nav-mobile-link-${l.id}`}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `rounded-2xl px-4 py-3.5 font-display text-2xl font-extrabold tracking-tight transition-colors ${
                                        isActive
                                            ? "bg-accent-soft text-accent"
                                            : "text-ink hover:bg-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex flex-col gap-4">
                        <a
                            href={SHOP.gofood}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-mobile-cta-gofood"
                            onClick={() => setOpen(false)}
                            className="btn-primary w-full"
                        >
                            <ShoppingBag className="h-4 w-4" /> Pesan di GoFood
                        </a>
                        <a
                            href={SHOP.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-mobile-link-instagram"
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-center gap-2 text-sm font-semibold text-ink-muted"
                        >
                            <Instagram className="h-4 w-4" /> {SHOP.instagramHandle}
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}